import { Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { SelectInput } from '@/components/form/baseInputs/SelectInput';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { Card } from '@/components/layout/Card/Card';

import { TileNotesLoadingOverlay } from './TileNotesLoadingOverlay';
import {
  TILE_NOTES_PROMPT_OPTIONS,
  TILE_NOTES_PROMPT_TEXTS,
  TileNotesPromptId,
} from './types';

export interface GenerateTileNotesCardProps {
  onGenerate: (prompt: string, promptId: TileNotesPromptId) => void;
  onCancel: () => void;
  isGenerating: boolean;
  currentStreamingNote?: string | null;
}

export function GenerateTileNotesCard({
  onGenerate,
  onCancel,
  isGenerating,
  currentStreamingNote,
}: GenerateTileNotesCardProps) {
  const [promptId, setPromptId] =
    useState<TileNotesPromptId>('trustees-access');
  const [promptText, setPromptText] = useState(
    TILE_NOTES_PROMPT_TEXTS['trustees-access'] ?? ''
  );

  useEffect(() => {
    if (promptId === 'custom') {
      setPromptText('');
    } else if (TILE_NOTES_PROMPT_TEXTS[promptId]) {
      setPromptText(TILE_NOTES_PROMPT_TEXTS[promptId] ?? '');
    }
  }, [promptId]);

  const handleGenerate = useCallback(() => {
    if (promptText.trim()) {
      onGenerate(promptText, promptId);
    }
  }, [onGenerate, promptText, promptId]);

  return (
    <Card variant="filled" sx={{ p: 2, position: 'relative' }}>
      <TileNotesLoadingOverlay
        open={isGenerating}
        currentTileNote={currentStreamingNote}
      />
      <Stack spacing={2}>
        <Typography variant="title-s">Generate tile notes</Typography>

        <SelectInput
          label="Select prompt"
          required
          options={TILE_NOTES_PROMPT_OPTIONS}
          value={promptId}
          onChange={(e) => setPromptId(e.target.value as TileNotesPromptId)}
          showEmptyValue={false}
        />

        <TextInput
          label="Prompt"
          required
          multiline
          autoFocus
          rows={4}
          placeholder="Describe the notes you would like to see added to the selected tiles"
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
        />

        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body-xs">
            Existing notes will be overridden
          </Typography>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="secondary" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              startIcon={Stars01Icon}
              iconProps={{ size: 16 }}
              onClick={handleGenerate}
              disabled={!promptText.trim() || isGenerating}
              loading={isGenerating}
            >
              Generate
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}
